import { IMAGE_EXTENSIONS, NON_IMAGE_EXTENSIONS } from "./file-types";

/**
 * Gets the file extension from a URL
 */
export function getFileExtension(url: string): string {
  try {
    const parsedUrl = new URL(decodeURIComponent(url));
    const pathname = parsedUrl.pathname;
    const extension = pathname.split(".").pop()?.toLowerCase() || "";
    return extension.match(/^[a-zA-Z0-9]+$/) ? `.${extension}` : "";
  } catch {
    return "";
  }
}

/**
 * Extracts a filename from a URL
 */
export function extractFileName(url: string): string {
  try {
    if (url.startsWith("temp")) {
      return "temp";
    }
    // First try to decode the URL to handle encoded characters
    const decodedUrl = decodeURIComponent(url);

    // Try to parse as URL to get the pathname
    const parsedUrl = new URL(decodedUrl);
    const pathname = parsedUrl.pathname;

    // Extract filename from the pathname
    const pathParts = pathname.split("/");
    const fileName = pathParts[pathParts.length - 1];

    // If we have a filename with extension, use it
    if (fileName && fileName.includes(".")) {
      return fileName;
    }

    // Build regex pattern from all supported extensions
    const allExtensions = [
      ...Array.from(IMAGE_EXTENSIONS),
      ...Array.from(NON_IMAGE_EXTENSIONS),
    ].join("|");
    const fileMatch = decodedUrl.match(
      new RegExp(`[^/\\\\]+\\.(${allExtensions})`, "i"),
    );
    if (fileMatch) {
      return fileMatch[0];
    }

    // If we still don't have a good filename, create a generic one
    const extension = getFileExtension(url);
    return `file${extension || ".txt"}`;
  } catch {
    // If all else fails, return a generic filename
    return "file.txt";
  }
}

/**
 * Checks if a URL points to an image file.
 *
 * `extensions` is overridable because each surface renders a different set
 * inline — the portal shows iPhone HEIC uploads as images, the inbox does not.
 */
export function isImageFile(
  url: string,
  extensions: ReadonlySet<string> = IMAGE_EXTENSIONS,
): boolean {
  try {
    const parsedUrl = new URL(url);
    const pathname = parsedUrl.pathname.toLowerCase();
    const extension = pathname.split(".").pop() || "";
    return extensions.has(extension);
  } catch {
    return false;
  }
}

/**
 * Separates files into images and non-image files
 */
export function separateFiles(
  files: string[] | null | undefined,
  extensions: ReadonlySet<string> = IMAGE_EXTENSIONS,
): {
  images: string[];
  nonImageFiles: string[];
} {
  if (!files || files.length === 0) {
    return { images: [], nonImageFiles: [] };
  }

  const sortedFiles = files.reduce(
    (acc: { images: string[]; nonImageFiles: string[] }, file) => {
      if (isImageFile(file, extensions)) {
        acc.images.push(file);
      } else {
        acc.nonImageFiles.push(file);
      }
      return acc;
    },
    { images: [], nonImageFiles: [] },
  );

  return sortedFiles;
}

/**
 * Checks if a message has any image files
 */
export function hasImages(
  files: string[] | null | undefined,
  extensions: ReadonlySet<string> = IMAGE_EXTENSIONS,
): boolean {
  if (!files || files.length === 0) return false;
  return files.some((file) => isImageFile(file, extensions));
}
